import { regions, years } from "@/mocks/videos";

export interface FilterState {
  year: string;
  region: string;
  sort: string;
}

interface CategoryFilterProps {
  value: FilterState;
  onChange: (next: FilterState) => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

const sortOptions = [
  { value: "default", label: "默认排序" },
  { value: "newest", label: "最新" },
  { value: "hot", label: "热门" },
];

const yearOptions = ["全部", ...years];
const regionOptions = ["全部", ...regions];

function OptionGroup({
  title,
  options,
  current,
  onSelect,
}: {
  title: string;
  options: string[];
  current: string;
  onSelect: (v: string) => void;
}) {
  return (
    <div className="mb-5 last:mb-0">
      <h3 className="mb-2.5 text-sm font-semibold text-foreground-800">
        {title}
      </h3>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onSelect(opt)}
            className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap transition-colors cursor-pointer ${
              current === opt
                ? "bg-primary-500 text-background-50 dark:text-foreground-950 font-medium"
                : "bg-background-100 text-foreground-600 hover:bg-background-200"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

function SortGroup({
  current,
  onSelect,
}: {
  current: string;
  onSelect: (v: string) => void;
}) {
  return (
    <div>
      <h3 className="mb-2.5 text-sm font-semibold text-foreground-800">排序</h3>
      <div className="flex flex-wrap gap-2">
        {sortOptions.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onSelect(opt.value)}
            className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap transition-colors cursor-pointer ${
              current === opt.value
                ? "bg-secondary-500 text-background-50 dark:text-foreground-950 font-medium"
                : "bg-background-100 text-foreground-600 hover:bg-background-200"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function CategoryFilter({
  value,
  onChange,
  mobileOpen,
  onCloseMobile,
}: CategoryFilterProps) {
  const set = (patch: Partial<FilterState>) => onChange({ ...value, ...patch });

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-64 shrink-0">
        <div className="sticky top-20 p-5 rounded-lg bg-background-50 border border-background-200">
          <div className="flex items-center gap-2 mb-5">
            <i className="ri-filter-3-line text-primary-600 text-lg"></i>
            <span className="font-semibold text-foreground-950">筛选条件</span>
          </div>
          <OptionGroup
            title="年份"
            options={yearOptions}
            current={value.year}
            onSelect={(v) => set({ year: v })}
          />
          <OptionGroup
            title="地区"
            options={regionOptions}
            current={value.region}
            onSelect={(v) => set({ region: v })}
          />
          <SortGroup current={value.sort} onSelect={(v) => set({ sort: v })} />
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div
            className="flex-1 bg-foreground-950/50"
            onClick={onCloseMobile}
            aria-hidden="true"
          ></div>
          <div className="w-[85%] max-w-sm h-full bg-background-50 p-5 overflow-y-auto animate-fade-in">
            <div className="flex items-center justify-between mb-5">
              <span className="font-semibold text-foreground-950">筛选条件</span>
              <button
                type="button"
                onClick={onCloseMobile}
                aria-label="关闭筛选"
                className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <OptionGroup
              title="年份"
              options={yearOptions}
              current={value.year}
              onSelect={(v) => set({ year: v })}
            />
            <OptionGroup
              title="地区"
              options={regionOptions}
              current={value.region}
              onSelect={(v) => set({ region: v })}
            />
            <SortGroup current={value.sort} onSelect={(v) => set({ sort: v })} />
            <button
              type="button"
              onClick={onCloseMobile}
              className="mt-6 w-full px-4 py-3 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold whitespace-nowrap cursor-pointer"
            >
              查看结果
            </button>
          </div>
        </div>
      )}
    </>
  );
}